/**
 * SocialCapital Org Dashboard — super-admin view of participating
 * organisations + credential minting.
 *
 * Route: /sc/org
 *
 * Super-admins can:
 *   • List / create organisations (name, type, country, trust level)
 *   • Mint one-off API keys (sandbox or production)
 *   • Mint OAuth2 client_credentials (client_id + client_secret)
 *   • Copy the raw secret ONCE (never returned again)
 *
 * Non-admins see a friendly "you need to be a super-admin" state with
 * a link to the API docs.
 */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, KeyRound, Shield, Copy, CheckCircle2, PlusCircle,
  Loader2, LogIn, BookOpen,
} from "lucide-react";
import SocialCapitalHeader from "@/components/socialcapital/SocialCapitalHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sc`;

const TRUST_LEVELS = [
  "pending", "registered", "verified_organization", "trusted_partner",
  "institutional_partner", "government_accredited", "strategic_partner",
];


export default function SocialCapitalOrgDashboard() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newSecret, setNewSecret] = useState(null); // {kind, org, raw}
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/organizations`, { withCredentials: true });
      setOrgs(r.data.items || []);
      setNeedsAuth(false);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setNeedsAuth(true);
      } else {
        setError("Failed to load organisations.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const mintApiKey = async (orgId, environment = "sandbox") => {
    try {
      const r = await axios.post(
        `${API}/admin/organizations/${orgId}/api-keys`,
        { environment, note: "Minted from dashboard" },
        { withCredentials: true },
      );
      const org = orgs.find((o) => o.id === orgId);
      setNewSecret({ kind: "API key", org, raw: r.data.raw_key });
    } catch {
      alert("Failed to mint API key");
    }
  };

  const mintOAuth = async (orgId, environment = "sandbox") => {
    try {
      const r = await axios.post(
        `${API}/admin/organizations/${orgId}/oauth-clients`,
        { environment },
        { withCredentials: true },
      );
      const org = orgs.find((o) => o.id === orgId);
      setNewSecret({
        kind: "OAuth2 client",
        org,
        raw: `client_id: ${r.data.client_id}\nclient_secret: ${r.data.client_secret}`,
      });
    } catch {
      alert("Failed to mint OAuth2 client");
    }
  };

  return (
    <div
      data-testid="sc-org-dashboard"
      className="min-h-screen"
      style={{ background: "var(--wofy-bg)" }}
    >
      <Helmet><title>Organisations · SocialCapital admin</title></Helmet>

      <SocialCapitalHeader />

      {/* Header */}
      <section
        className="border-b"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-12 pb-8">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-5 text-[11px] font-mono uppercase tracking-[0.18em]"
            style={{
              background: "rgba(139,92,246,0.09)",
              color: "#7C3AED",
              border: "1px solid rgba(139,92,246,0.24)",
            }}
          >
            <Shield className="w-3 h-3" /> Super-admin console
          </div>
          <h1
            className="font-display text-3xl md:text-4xl leading-tight"
            style={{ color: "var(--wofy-text)" }}
          >
            Participating organisations
          </h1>
          <p
            className="mt-2 text-[15px]"
            style={{ color: "var(--wofy-text-soft)" }}
          >
            Register partners, set their trust level, and mint credentials for the
            external API. Every credential is hashed at rest — copy the raw value
            immediately, we can't show it again.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-8">
        {needsAuth ? (
          <div
            data-testid="needs-admin"
            className="rounded-md border p-8 text-center"
            style={{ borderColor: "var(--wofy-border)", background: "white" }}
          >
            <p className="text-[15px] mb-4" style={{ color: "var(--wofy-text)" }}>
              This console is only available to SocialPulse super-admins.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link
                to="/login?returnTo=/sc/org"
                className="wofy-btn-primary inline-flex items-center gap-2"
                style={{ background: "#7C3AED", borderColor: "#7C3AED" }}
              >
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
              <Link
                to="/sc/docs"
                className="wofy-btn-ghost inline-flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" /> Read the API docs
              </Link>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 text-sm opacity-70">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading organisations…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div className="text-[11px] font-mono uppercase tracking-[0.15em] opacity-60">
                {orgs.length} organisation{orgs.length === 1 ? "" : "s"} registered
              </div>
              <button
                data-testid="new-org-btn"
                onClick={() => setShowCreate(true)}
                className="wofy-btn-primary inline-flex items-center gap-2"
                style={{ background: "#7C3AED", borderColor: "#7C3AED" }}
              >
                <PlusCircle className="w-4 h-4" /> Register organisation
              </button>
            </div>

            {error && (
              <div
                className="rounded-md border px-4 py-3 mb-4 text-[13px]"
                style={{
                  borderColor: "rgba(220,38,38,0.35)",
                  background: "rgba(220,38,38,0.05)",
                  color: "#B91C1C",
                }}
              >
                {error}
              </div>
            )}

            {orgs.length === 0 ? (
              <div
                data-testid="orgs-empty"
                className="rounded-md border border-dashed p-10 text-center text-[14px]"
                style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
              >
                No partners registered yet. Click "Register organisation" to add the first one.
              </div>
            ) : (
              <div
                data-testid="orgs-grid"
                className="grid gap-3"
              >
                {orgs.map((org) => (
                  <OrgRow
                    key={org.id}
                    org={org}
                    onMintApiKey={() => mintApiKey(org.id, org.api_environment)}
                    onMintOAuth={() => mintOAuth(org.id, org.api_environment)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {newSecret && (
        <SecretModal
          secret={newSecret}
          onClose={() => setNewSecret(null)}
        />
      )}
    </div>
  );
}


function OrgRow({ org, onMintApiKey, onMintOAuth }) {
  return (
    <div
      data-testid={`org-row-${org.slug}`}
      className="rounded-md border p-5 flex flex-col md:flex-row md:items-center gap-4"
      style={{ borderColor: "var(--wofy-border)", background: "white" }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-md"
          style={{ background: "rgba(139,92,246,0.10)", color: "#7C3AED" }}
        >
          <Building2 className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <div
            className="font-display text-[17px] leading-tight truncate"
            style={{ color: "var(--wofy-text)" }}
          >
            {org.name}
          </div>
          <div className="text-[12px] font-mono opacity-70 mt-0.5 truncate">
            {org.slug} · {org.type}
            {org.country_code ? ` · ${org.country_code}` : ""}
            {" · "}<span style={{ color: "#7C3AED" }}>{org.trust_level}</span>
            {" · "}{org.api_environment}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid={`mint-apikey-${org.slug}`}
          onClick={onMintApiKey}
          className="wofy-btn-ghost inline-flex items-center gap-1.5 text-[13px]"
        >
          <KeyRound className="w-3.5 h-3.5" /> Mint API key
        </button>
        <button
          data-testid={`mint-oauth-${org.slug}`}
          onClick={onMintOAuth}
          className="wofy-btn-ghost inline-flex items-center gap-1.5 text-[13px]"
        >
          <Shield className="w-3.5 h-3.5" /> Mint OAuth client
        </button>
      </div>
    </div>
  );
}


function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", type: "nonprofit", country_code: "", contact_email: "",
    trust_level: "registered", api_environment: "sandbox",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const payload = { ...form };
      if (!payload.country_code) delete payload.country_code;
      await axios.post(`${API}/admin/organizations`, payload, { withCredentials: true });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create organisation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,15,20,0.55)" }}
    >
      <form
        data-testid="new-org-form"
        onSubmit={submit}
        className="w-full max-w-md rounded-md border bg-white p-6"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <h3
          className="font-display text-xl mb-4"
          style={{ color: "var(--wofy-text)" }}
        >
          Register organisation
        </h3>
        <FormRow label="Name">
          <input
            required
            data-testid="new-org-name"
            className="wofy-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </FormRow>
        <FormRow label="Type">
          <select
            className="wofy-input"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {["nonprofit","school","university","charity","municipality","company","hospital","government_agency","other"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </FormRow>
        <FormRow label="Country (ISO-2, optional)">
          <input
            data-testid="new-org-country"
            className="wofy-input uppercase"
            maxLength={2}
            value={form.country_code}
            onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })}
          />
        </FormRow>
        <FormRow label="Contact email">
          <input
            required
            type="email"
            className="wofy-input"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          />
        </FormRow>
        <FormRow label="Trust level">
          <select
            data-testid="new-org-trust"
            className="wofy-input"
            value={form.trust_level}
            onChange={(e) => setForm({ ...form, trust_level: e.target.value })}
          >
            {TRUST_LEVELS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </FormRow>
        <FormRow label="Environment">
          <select
            className="wofy-input"
            value={form.api_environment}
            onChange={(e) => setForm({ ...form, api_environment: e.target.value })}
          >
            <option value="sandbox">sandbox</option>
            <option value="production">production</option>
          </select>
        </FormRow>

        {err && <div className="text-[12px] mb-2" style={{ color: "#B91C1C" }}>{err}</div>}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            data-testid="new-org-submit"
            className="wofy-btn-primary inline-flex items-center gap-2"
            style={{ background: "#7C3AED", borderColor: "#7C3AED" }}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Register
          </button>
        </div>
      </form>
    </div>
  );
}


function FormRow({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="text-[11px] font-mono uppercase tracking-[0.15em] opacity-70 block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}


function SecretModal({ secret, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret.raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,15,20,0.55)" }}
    >
      <div
        data-testid="secret-modal"
        className="w-full max-w-lg rounded-md border bg-white p-6"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <h3
          className="font-display text-xl mb-1"
          style={{ color: "var(--wofy-text)" }}
        >
          {secret.kind} minted for {secret.org?.name}
        </h3>
        <p
          className="text-[13px] mb-4"
          style={{ color: "var(--wofy-text-soft)" }}
        >
          Copy this now. We hash it at rest — this is the only time we can show the raw value.
        </p>
        <div
          className="rounded-md border font-mono text-[12px] p-3 mb-3 whitespace-pre-wrap break-all"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
        >
          {secret.raw}
        </div>
        <div className="flex justify-end gap-2">
          <button
            data-testid="copy-secret-btn"
            onClick={copy}
            className="wofy-btn-ghost inline-flex items-center gap-2"
          >
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={onClose}
            className="wofy-btn-primary"
            data-testid="secret-close-btn"
            style={{ background: "#7C3AED", borderColor: "#7C3AED" }}
          >
            I've saved it
          </button>
        </div>
      </div>
    </div>
  );
}
